import { useRef, useEffect, useCallback } from "react";

const ITEM_H = 44;
const VISIBLE = 3;
const CONTAINER_H = ITEM_H * VISIBLE;

interface WheelPickerProps {
  values: number[];
  selected: number;
  onChange: (v: number) => void;
  suffix?: string;
  accentClass?: string;
}

export function WheelPicker({
  values,
  selected,
  onChange,
  suffix,
  accentClass = "text-vajra-cyan",
}: WheelPickerProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isUserScrollRef = useRef(false);

  function snapTo(idx: number, smooth = false) {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({ top: idx * ITEM_H, behavior: smooth ? "smooth" : "instant" });
  }

  useEffect(() => {
    const idx = values.indexOf(selected);
    if (idx >= 0) snapTo(idx, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isUserScrollRef.current) return;
    const idx = values.indexOf(selected);
    if (idx >= 0) snapTo(idx, true);
  }, [selected, values]);

  const handleScroll = useCallback(() => {
    isUserScrollRef.current = true;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!scrollRef.current) return;
      const idx = Math.round(scrollRef.current.scrollTop / ITEM_H);
      const clamped = Math.max(0, Math.min(values.length - 1, idx));
      snapTo(clamped, true);
      onChange(values[clamped]);
      setTimeout(() => { isUserScrollRef.current = false; }, 250);
    }, 120);
  }, [values, onChange]);

  return (
    <div
      className="relative select-none"
      style={{
        height: CONTAINER_H,
        maskImage: "linear-gradient(to bottom, transparent 0%, black 33%, black 67%, transparent 100%)",
        WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 33%, black 67%, transparent 100%)",
      }}
    >
      {/* Selection highlight */}
      <div
        className="absolute inset-x-0 z-0 pointer-events-none border-y border-gray-200 dark:border-[#1E3A5F]"
        style={{ top: ITEM_H, height: ITEM_H }}
      />
      {/* Scrollable list */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="h-full overflow-y-scroll"
        style={{
          scrollSnapType: "y mandatory",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div style={{ height: ITEM_H, scrollSnapAlign: "center" }} />
        {values.map((v) => {
          const isSelected = v === selected;
          return (
            <div
              key={v}
              style={{ height: ITEM_H, scrollSnapAlign: "center" }}
              className={`flex items-center justify-center gap-1 transition-all ${
                isSelected
                  ? `text-xl font-bold ${accentClass}`
                  : "text-base font-medium text-gray-400 dark:text-gray-600"
              }`}
            >
              {v}
              {suffix && isSelected && (
                <span className="text-sm font-normal opacity-60 ml-0.5">{suffix}</span>
              )}
            </div>
          );
        })}
        <div style={{ height: ITEM_H, scrollSnapAlign: "center" }} />
      </div>
    </div>
  );
}
